import React, { useState, useRef } from "react";
import FilterNode from "./FilterNode";
import useIsMobile from "./useIsMobile";

import RenderDrillDown from "./RenderDrillDown";
import RenderFilterTree from "./RenderFilterTree";
import { BrandModelResultModel } from "./types/BrandModelResultModel";

interface FilterComponentProps {
  data?: BrandModelResultModel[];
  filterLevel?: number;
  showSearch?: boolean;
  onFiltered?: (
    node: BrandModelResultModel,
    nodePath: string,
    activate: boolean,
    fieldname?: string
  ) => void;
  displayNames?: string[];
  filters?: Record<string, any>;
  displayFullPathForSearch?: boolean;
}

// Recursive component for desktop tree
const FilterComponent = ({
  data = [],
  filterLevel,
  showSearch,
  onFiltered,
  displayNames = [],
  filters = {},
  displayFullPathForSearch = false,
}: FilterComponentProps) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [drillPath, setDrillPath] = useState([]); // for mobile drill-down

  // Anchor ref for scroll context
  const anchorRef = useRef<HTMLDivElement>(null);

  // Helper to find all active filter paths from data
  const getActiveFilterPaths = (
    nodes: BrandModelResultModel[],
    path = ""
  ): string[] => {
    let paths: string[] = [];
    nodes.forEach((node, idx) => {
      const nodePath = path === "" ? String(idx) : `${path}-${idx}`;
      if (node.currentIsFilterable && node.activated) {
        paths.push(nodePath);
      }
      if (node.children && node.children.length > 0) {
        paths = paths.concat(getActiveFilterPaths(node.children, nodePath));
      }
    });
    return paths;
  };
  const activeFilterPaths = getActiveFilterPaths(data);

  const handleToggle = (nodePath: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodePath)) {
        newSet.delete(nodePath);
      } else {
        newSet.add(nodePath);
      }
      return newSet;
    });
  };

  // Add fieldname to handleFiltered and pass to onFiltered
  const handleFiltered = (
    node: BrandModelResultModel,
    nodePath: string,
    activate: boolean
  ) => {
    if (onFiltered) onFiltered(node, nodePath, activate, node.fieldname);
    // Focus anchor to maintain scroll context
    if (anchorRef.current) {
      anchorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Helper to build a deduplicated list of active filters
  function getUniqueActiveFilters(
    data: BrandModelResultModel[],
    displayNames: string[] = []
  ) {
    const seen = new Set();
    const result: {
      fieldname: string;
      value: string;
      displayName: string;
      node: BrandModelResultModel;
    }[] = [];
    function traverse(nodes: BrandModelResultModel[]) {
      nodes.forEach((node) => {
        if (node.currentIsFilterable && node.activated) {
          const key = node.fieldname + "::" + node.text;
          if (!seen.has(key)) {
            seen.add(key);
            result.push({
              fieldname: node.fieldname,
              value: node.text,
              displayName:
                displayNames && displayNames.length > 0
                  ? displayNames[result.length] || node.fieldname
                  : node.fieldname,
              node,
            });
          }
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }
    traverse(data);
    return result;
  }

  // Helper to render selected filters with display names
  const renderSelectedFilters = () => {
    const uniqueActiveFilters = getUniqueActiveFilters(data, displayNames);
    if (!uniqueActiveFilters.length) return null;
    return (
      <div>
        <ul
          className="active-filters-list"
          style={{ listStyle: "none", padding: 0, margin: "8px 0" }}
        >
          {uniqueActiveFilters.map((filter) => (
            <li
              key={filter.fieldname + ":" + filter.value}
              className="active-filter-chip"
              style={{ cursor: "pointer", marginRight: 8 }}
              onClick={() => {
                if (onFiltered)
                  onFiltered(filter.node, "", false, filter.fieldname);
              }}
            >
              <span style={{ textDecoration: "underline", color: "#1976d2" }}>
                {filter.value}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Helper: returns true if node or any descendant is filterable and active
  function hasAnyFilterableDescendant(node: BrandModelResultModel): boolean {
    if (node.currentIsFilterable) return true;
    if (node.children && node.children.length > 0) {
      return node.children.some((child) => hasAnyFilterableDescendant(child));
    }
    return false;
  }

  // Modified FilterNode wrapper to skip rendering nodes with no filterable descendants
  const RenderNode = (props: {
    node: BrandModelResultModel;
    nodePath: string;
  }) => {
    if (!hasAnyFilterableDescendant(props.node)) return null;
    return (
      <FilterNode
        {...props}
        filters={filters}
        onFiltered={handleFiltered}
        searchTerm={searchTerm}
        isActive={activeFilterPaths.includes(props.nodePath)}
      />
    );
  };

  // Helper to find all filterable items matching the search term and deduplicate by text
  const getFilterableSearchResults = (
    nodes: BrandModelResultModel[],
    path = "",
    results: {
      node: BrandModelResultModel;
      nodePath: string;
      pathArray: number[];
    }[] = []
  ) => {
    nodes.forEach((node, idx) => {
      const nodePath = path === "" ? String(idx) : `${path}-${idx}`;
      if (
        node.currentIsFilterable &&
        node.text.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        results.push({
          node,
          nodePath,
          pathArray: nodePath.split("-").map(Number),
        });
      }
      if (node.children && node.children.length > 0) {
        getFilterableSearchResults(node.children, nodePath, results);
      }
    });
    return results;
  };

  // Deduplicate search results by node text, keeping the first occurrence
  const getDeduplicatedSearchResults = () => {
    const allResults = getFilterableSearchResults(data);
    const seen = new Set();
    const deduplicated: {
      node: BrandModelResultModel;
      nodePath: string;
      pathArray: number[];
    }[] = [];

    allResults.forEach((result) => {
      if (!seen.has(result.node.text)) {
        seen.add(result.node.text);
        deduplicated.push(result);
      }
    });

    return deduplicated;
  };

  // Helper to recursively find all filterable items matching the search term in a subtree
  const findMatchingFilterable = (
    nodes: BrandModelResultModel[],
    path: number[] = [],
    matches: { node: BrandModelResultModel; path: number[] }[] = []
  ) => {
    nodes.forEach((node, idx) => {
      const currentPath = [...path, idx];
      if (
        node.currentIsFilterable &&
        node.text.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        matches.push({ node, path: currentPath });
      }
      if (node.children && node.children.length > 0) {
        findMatchingFilterable(node.children, currentPath, matches);
      }
    });
    return matches;
  };

  // return 1 === 2 ? (
  //   <RenderDrillDown
  //     data={data}
  //     filterLevel={filterLevel}
  //     showSearch={showSearch}
  //     searchTerm={searchTerm}
  //     setSearchTerm={setSearchTerm}
  //     drillPath={drillPath}
  //     setDrillPath={setDrillPath}
  //     getDeduplicatedSearchResults={getDeduplicatedSearchResults}
  //     handleFiltered={handleFiltered}
  //     renderSelectedFilters={renderSelectedFilters}
  //     findMatchingFilterable={findMatchingFilterable}
  //   />
  // ) : (
  return (
    <>
      {/* Visually hidden anchor for scroll context */}
      <div
        ref={anchorRef}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          outline: "none",
        }}
      />
      <RenderFilterTree
        data={data}
        filterLevel={filterLevel}
        filters={filters}
        displayFullPathForSearch={displayFullPathForSearch}
        searchTerm={searchTerm}
        handleFiltered={handleFiltered}
        RenderNode={RenderNode}
        expandedNodes={expandedNodes}
        handleToggle={handleToggle}
        getDeduplicatedSearchResults={getDeduplicatedSearchResults}
        activeFilterPaths={activeFilterPaths}
        renderSelectedFilters={renderSelectedFilters}
        showSearch={showSearch}
        setSearchTerm={setSearchTerm}
      />
    </>
  );
  // );
};

export default FilterComponent;
